import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useDispatch, useSelector } from 'react-redux';
import { subDays } from 'date-fns';
import { useLocation } from 'react-router';

import { SplashScreen } from 'src/components/loading-screen';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { CountryBar } from 'src/components/country-bar';
import { useCountryData } from 'src/components/visibility/hooks/useCountryData';
import { HEADER } from 'src/layouts/config-layout';
import { CountryTabLabel } from '../explore/CountryTabLabel';
import {
  setFilterCompare,
  setFilterDate,
  setFilterDateCompare,
  setFilterMinDatePicker,
  setFilterTimestep,
} from 'src/context/reducers/filter-settings/filterSlice';
import { getDateRangeIso, getLast12WeeksDateRange } from 'src/utils/format-time';

import FunnelKpiCards from './FunnelKpiCards';
import { useFunnelKpis } from './hooks/useFunnelKpis';
import FunnelDistributionChart from './FunnelDistributionChart';
import FunnelPerformanceChart from './FunnelPerformanceChart';
import ConversionsAnalysis from '../conversionsAnalysis';
import { PAGE_PADDING_X, SECTION_GAP, COUNTRY_UNDERLINE } from './constants';

export default function FunnelView() {
  const dispatch = useDispatch();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const location = useLocation();
  const [selectedDisplayGraph, setSelectedDisplayGraphValue] = useState('Value');
  const [selectedCountry, setSelectedCountry] = useState<string | null>('ALL');

  // Pays + couverture % via la query Visibility (front-only), comme sur Explore : ça
  // fournit `share_of_brand_searches` pour afficher le % dans CountryTabLabel. Triés
  // couverture desc. Pas de "freeze" comme Explore : le Funnel ne dispatch pas
  // setFilterCountry (sélection locale via useFunnelKpis), la liste ne se réduit donc pas.
  const { countriesData: countries, isLoading: isLoadingCountries } = useCountryData();

  const {
    mainData: kpisData,
    compareData,
    error: kpisError,
    isFetching: isFetchingKpis,
  } = useFunnelKpis(selectedCountry, workspace);

  useEffect(() => {
    if (kpisData?.min_date) {
      dispatch(setFilterMinDatePicker(kpisData.min_date));
    }
  }, [kpisData?.min_date, dispatch]);

  useEffect(() => {
    if (filterStore?.searchPerfDateOverridden) return; // keep the persisted override
    const { start, end } = getLast12WeeksDateRange();
    dispatch(setFilterDate({ start, end }));
    dispatch(setFilterTimestep('year_week'));
    dispatch(setFilterCompare(false));
    dispatch(
      setFilterDateCompare({
        compareStart: subDays(new Date(), 60).toISOString(),
        compareEnd: subDays(new Date(), 31).toISOString(),
      })
    );
  }, []);

  useEffect(() => {
    if (workspace?.name?.includes('Demo')) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { start, end } = getDateRangeIso(new Date(2024, 8, 1), new Date(2024, 10, 30), tz);
      dispatch(setFilterDate({ start, end }));
      dispatch(setFilterTimestep('date_day'));
    }
  }, [workspace, location?.pathname]);

  if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
    return <SplashScreen />;
  }

  return (
    <>
      <CountryBar
        countries={countries}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
        includeAll
        loading={isLoadingCountries || (countries.length === 0 && isFetchingKpis)}
        // +6 vs the old HEADER.H_DESKTOP*3: the secondary nav grew (title 52→54,
        // filter bar 52→56), so the bar and its content spacer shift down to match.
        stickyTop={HEADER.H_DESKTOP * 3 + 6}
        backgroundColor="background.paper"
        topGap={60}
        contentPaddingX={PAGE_PADDING_X}
        selectedColor={COUNTRY_UNDERLINE}
        selectedBorderWidth={2}
        renderCountryLabel={(c, isSelected) => (
          <CountryTabLabel country={c} isSelected={isSelected} />
        )}
      />
      <Box px={PAGE_PADDING_X}>
      <FunnelKpiCards
        kpisData={kpisData}
        compareData={compareData}
        kpisError={kpisError}
        isFetchingKpis={isFetchingKpis}
        workspace={workspace}
      />
      <div>
        <Grid container spacing={SECTION_GAP}>
          <Grid xs={12} md={6}>
            <FunnelDistributionChart
              kpisData={kpisData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
              selectedDisplayGraph={selectedDisplayGraph}
              setSelectedDisplayGraphValue={setSelectedDisplayGraphValue}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FunnelPerformanceChart
              kpisData={kpisData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
            />
          </Grid>
        </Grid>
      </div>
      <ConversionsAnalysis />
    </Box>
    </>
  );
}
